import { apiClient } from './api';

export type JobSearchStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export const jobSearchStatusLabel: Record<JobSearchStatus, string> = {
  pending: 'در صف',
  running: 'در حال اجرا',
  completed: 'تمام‌شده',
  failed: 'ناموفق',
  cancelled: 'لغوشده',
};

export type JobSearch = {
  id: string;
  sourceSlug: string;
  keywords: string[];
  cities: string[];
  seniority: string;
  employmentType: string;
  workArrangement: string;
  requestedLimit: number;
  useProfile: boolean;
  enableMatch: boolean;
  useAi: boolean;
  minMatch: number;
  status: JobSearchStatus;
  errorMessage: string;
  pagesCrawled: number;
  jobsFound: number;
  jobsSaved: number;
  jobsSkipped: number;
  matchesWritten: number;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
};

export type JobItem = {
  id: string;
  sourceSlug: string;
  externalJobId: string;
  canonicalUrl: string;
  title: string;
  company: string;
  locationRaw: string;
  locationNormalized: string;
  workArrangement: string;
  seniority: string;
  employmentType: string;
  salaryRaw: string;
  status: string;
  publishedAt?: string | null;
  matchScore: number;
  matchReasoning: string;
  /** rejected = hard reject; unscored = no match row; scored = formula score */
  matchKind?: 'rejected' | 'unscored' | 'scored';
  matchModel: string;
  createdAt: string;
};

type PageResult<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages?: number;
};

export const jobService = {
  async listSources() {
    const res = await apiClient.get<Array<Record<string, unknown>>>('/admin/jobs/sources');
    return res.data ?? [];
  },

  async listProfiles() {
    const res = await apiClient.get<Array<Record<string, unknown>>>('/admin/jobs/profiles');
    return res.data ?? [];
  },

  async listSearches(params?: { page?: number; limit?: number; status?: string }) {
    const res = await apiClient.get<PageResult<JobSearch>>('/admin/jobs/searches', { params });
    return res.data;
  },

  async createSearch(data: Record<string, unknown>) {
    const res = await apiClient.post<JobSearch>('/admin/jobs/searches', data);
    return res.data;
  },

  async getSearch(id: string) {
    const res = await apiClient.get<JobSearch>(`/admin/jobs/searches/${id}`);
    return res.data;
  },

  async cancelSearch(id: string) {
    const res = await apiClient.post<JobSearch>(`/admin/jobs/searches/${id}/cancel`, {});
    return res.data;
  },

  async deleteSearch(id: string) {
    await apiClient.delete(`/admin/jobs/searches/${id}`);
  },

  async listJobs(params?: {
    page?: number;
    limit?: number;
    source?: string;
    location?: string;
    seniority?: string;
    q?: string;
    minScore?: number;
    /** recommended (default) applies minScore; all is the debug listing */
    view?: 'recommended' | 'all';
    /** score (default) | date | published */
    sort?: 'score' | 'date' | 'published';
  }) {
    const res = await apiClient.get<PageResult<JobItem>>('/admin/jobs', { params });
    return res.data;
  },

  async getJob(id: string) {
    const res = await apiClient.get<Record<string, unknown>>(`/admin/jobs/${id}`);
    return res.data;
  },

  async deleteJob(id: string) {
    await apiClient.delete(`/admin/jobs/${id}`);
  },
};
